import { css, Interpolation, Theme } from '@emotion/react'
import React, { ReactNode, useEffect } from 'react'

import iconHumbergerCloseUrl from '../../images/charm_menu-hamburger-closr.svg'
import modalCloseUrl from '../../images/modal-close.svg'
import { mq, rem } from '../../styles/mixin.styles'
import { colors, mobileSafeSize, space } from '../../styles/variables.styles'
import { ButtonLink } from './ButtonLink'

type ModalProps = {
  openModal: boolean
  setOpenModal: React.Dispatch<React.SetStateAction<boolean>>
  children: ReactNode
  message?: ReactNode
  title?: string
  link?: string
  linkName?: string
  subtitle?: string
  style?: `default` | `video`
}

/**
 * Modal
 *
 * @param ModalProps card props
 * @returns Modal contents
 */
export const Modal = ({
  title,
  subtitle,
  message,
  link,
  linkName,
  children,
  openModal,
  setOpenModal,
  style
}: ModalProps) => {
  useEffect(() => {
    const scrollY = window.scrollY
    console.log(scrollY)
    const body = document.body
    if (openModal) {
      document.body.classList.add('no-scroll')
      body.style.top = `-${scrollY}px`
    } else {
      const preScrollY = body.style.top
      document.body.classList.remove('no-scroll')
      window.scrollTo(0, parseInt(preScrollY) * -1)
    }
  }, [openModal])

  /**
   * modalStyle
   *
   * @return css styles
   */
  const modalStyle = (): Interpolation<Theme> => {
    if (style === `video`) {
      return videoModalStyle
    } else {
      return defaultModalStyle
    }
  }

  return (
    <div
      css={openModal ? modalStyle : closeModalStyle}
      id="modal01"
      onClick={() => {
        setOpenModal(false)
      }}
    >
      <section className="modal">
        <button
          className="close-button"
          aria-expanded={openModal}
          aria-controls="modal01"
          onClick={() => setOpenModal(!openModal)}
        >
          メッセージを閉じる
        </button>
        {title && <h1 className="modal__title">{title}</h1>}
        {subtitle && <p className="modal__sub-title">{subtitle}</p>}
        {message && <p className="modal__message">{message}</p>}
        {children}
        {link && <ButtonLink link={link}>{linkName}</ButtonLink>}
      </section>
    </div>
  )
}

const defaultModalStyle = css`
  background-color: rgba(${colors.black.rgb}, 0.7);
  display: flex;
  position: fixed;
  z-index: 1000;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  justify-content: center;
  align-items: center;
  width: 100vw;
  width: 100dvw;
  height: 100vh;
  height: 100dvh;
  video {
    max-width: 100%;
  }
  .modal {
    background-color: ${colors.white.hex};
    width: min(calc(100% - ${space.md} * 2), ${rem(570)});
    margin: auto;
    position: relative;
    padding: calc(${space.md} * 4) ${space.md};
    text-align: center;
    font-size: clamp(${rem(14)}, 3vw, ${rem(16)});
    .close-button {
      position: absolute;
      top: ${rem(8)};
      right: ${rem(11)};
      z-index: 1001;
      color: transparent;
      width: ${rem(mobileSafeSize)};
      height: ${rem(mobileSafeSize)};
      background-image: url(${iconHumbergerCloseUrl});
      background-position: center;
      background-size: ${rem(27)};
    }
    .modal__title {
      color: ${colors.mainBlue.hex};
      font-size: clamp(${rem(22)}, 5vw, ${rem(24)});
      line-height: clamp(${rem(32)}, 5vw, ${rem(35)});
      + .modal__sub-title {
        margin-top: ${space.md};
      }
    }
    .modal__sub-title {
      font-weight: 700;
      + p {
        margin-top: ${space.sm};
      }
    }
    > p + * {
      margin-top: ${rem(38)};
    }
  }
  ${mq(`lg`)} {
    background-color: rgba(${colors.black.rgb}, 0.6);
    .modal {
      .close-button {
        background-size: ${rem(36)};
      }
      p {
        br {
          display: none;
        }
      }
    }
  }
`

const videoModalStyle = css`
  ${defaultModalStyle}
  .modal {
    padding: ${rem(56)} 0;
    width: min(calc(100% - ${space.md} * 2), ${rem(1000)});
    background-color: transparent;
    .close-button {
      top: ${rem(10)};
      right: ${rem(-6)};
      background-image: url(${modalCloseUrl});
      background-size: ${rem(36)};
      &:hover {
        opacity: 0.8;
      }
    }
  }
  ${mq('lg')} {
    .modal {
      .close-button {
        background-size: ${rem(40)};
        top: ${rem(4)};
      }
    }
  }
`

const closeModalStyle = css`
  display: none;
`
